import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Table, Button, Card, Spinner, Alert, Form, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faPlus, faSearch, faToggleOn, faToggleOff } from '@fortawesome/free-solid-svg-icons';
import {
  fetchProducts,
  addProduct,
  updateProduct,
  deleteProduct,
  toggleProductActive
} from '../../redux/actions/productActions';
import Pagination from '../common/Pagination';
import useDebounce from '../../hooks/useDebounce';
import { hasEditPermission, hasDeletePermission } from '../../utils/authConfig';
import ProductForm from './ProductForm';
import {
  ListDesktop,
  ListMobile,
  MobileCard,
  MobileCardTop,
  MobileCardTitle,
  MobileCardRow,
  MobileCardFooter,
} from '../common/ListMobileLayout';

const ProductList = () => {
  const dispatch = useDispatch();
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [searchQuery, setSearchQuery] = useState('');
  const [formLoading, setFormLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all'); // all, active, inactive

  // Check permissions
  const canEdit = hasEditPermission();
  const canDelete = hasDeletePermission();

  // Debounce the search query with a 500ms delay
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const { items: products, loading, total, error, pagination } = useSelector((state) => ({
    items: state.products.items || [],
    loading: state.products.loading,
    total: state.products.total || 0,
    error: state.products.error,
    pagination: state.products.pagination || {}
  }));

  useEffect(() => {
    const params = {
      page: currentPage,
      per_page: pageSize
    };

    if (debouncedSearchQuery) {
      params.search = debouncedSearchQuery;
    }

    if (statusFilter !== 'all') {
      params.status = statusFilter;
    }

    dispatch(fetchProducts(params));
  }, [dispatch, currentPage, pageSize, debouncedSearchQuery, statusFilter]);

  const handleShowModal = (product = null) => {
    setEditingProduct(product);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingProduct(null);
  };

  const handleSubmit = async (formData) => {
    setFormLoading(true);
    try {
      if (editingProduct) {
        await dispatch(updateProduct(editingProduct.product_id, formData));
      } else {
        await dispatch(addProduct(formData));
      }
      handleCloseModal();
    } catch (error) {
      console.error('Failed to save product:', error);
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (productId) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await dispatch(deleteProduct(productId));
      } catch (error) {
        console.error('Failed to delete product:', error);
      }
    }
  };

  const handleToggleActive = async (productId) => {
    try {
      await dispatch(toggleProductActive(productId));
    } catch (error) {
      console.error('Failed to toggle product status:', error);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1); // Reset to first page when filtering
  };

  const formatPrice = (price, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(price);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (error) {
    return (
      <Alert variant="danger" className="mt-3">
        {error}
      </Alert>
    );
  }

  return (
    <div className="dashboard-list-page">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Products</h2>
        {canEdit && (
          <Button variant="primary" onClick={() => handleShowModal()}>
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Add Product
          </Button>
        )}
      </div>

      <Card>
        <Card.Body>
          <div className="d-flex gap-3 mb-4">
            <div className="flex-grow-1">
              <div className="input-group">
                <span className="input-group-text">
                  <FontAwesomeIcon icon={faSearch} />
                </span>
                <Form.Control
                  type="text"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                />
              </div>
            </div>
            <div className="col-md-3">
              <Form.Select
                value={statusFilter}
                onChange={handleStatusFilterChange}
              >
                <option value="all">All Products</option>
                <option value="active">Active Only</option>
                <option value="inactive">Inactive Only</option>
              </Form.Select>
            </div>
          </div>

          <div className="list-pagination-top">
            <Pagination
              currentPage={currentPage}
              totalItems={total}
              itemsPerPage={pageSize}
              onPageChange={handlePageChange}
              position="top"
            />
          </div>

          <ListDesktop>
            <Table hover className="mb-0">
              <thead>
                <tr>
                  <th className="table-col-id">ID</th>
                  <th className="table-col-product-name">Name</th>
                  <th className="table-col-product-description">Description</th>
                  <th className="table-col-product-price">Price</th>
                  <th className="table-col-product-credits">Credits</th>
                  <th className="table-col-product-status">Status</th>
                  <th className="table-col-product-created">Created</th>
                  <th className="table-col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="8" className="text-center">
                      <div className="loading-spinner">
                        <Spinner animation="border" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </Spinner>
                      </div>
                    </td>
                  </tr>
                ) : (
                  products.map((product) => (
                    <tr key={product.product_id}>
                      <td className="table-col-id">{product.product_id}</td>
                      <td className="table-col-product-name">{product.name}</td>
                      <td className="table-col-product-description">
                        {product.description ? (
                          <div className="text-truncate" style={{ maxWidth: '200px' }} title={product.description}>
                            {product.description}
                          </div>
                        ) : (
                          <span className="text-muted">No description</span>
                        )}
                      </td>
                      <td className="table-col-product-price">
                        {product.price ? formatPrice(product.price, product.currency) : 'N/A'}
                      </td>
                      <td className="table-col-product-credits">
                        {product.credit_amount || 0}
                      </td>
                      <td className="table-col-product-status">
                        <Badge
                          bg={product.is_active ? 'success' : 'secondary'}
                          className={product.is_active ? '' : 'text-white'}
                        >
                          {product.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td className="table-col-product-created">
                        {product.created_at ? formatDate(product.created_at) : 'N/A'}
                      </td>
                      <td className="table-col-actions">
                        <div className="d-flex gap-1">
                          {canEdit && (
                            <>
                              <Button 
                                variant="outline-primary" 
                                size="sm"
                                onClick={() => handleShowModal(product)}
                                title="Edit Product"
                              >
                                <FontAwesomeIcon icon={faEdit} />
                              </Button>
                              <Button 
                                variant={product.is_active ? "outline-warning" : "outline-success"} 
                                size="sm"
                                onClick={() => handleToggleActive(product.product_id)}
                                title={product.is_active ? "Deactivate" : "Activate"}
                              >
                                <FontAwesomeIcon icon={product.is_active ? faToggleOff : faToggleOn} />
                              </Button>
                            </>
                          )}
                          {canDelete && (
                            <Button 
                              variant="outline-danger" 
                              size="sm"
                              onClick={() => handleDelete(product.product_id)}
                              title="Delete Product"
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </ListDesktop>

          {!loading && (
            <ListMobile>
              {products.map((product) => (
                <MobileCard key={product.product_id}>
                  <MobileCardTop>
                    <MobileCardTitle>{product.name}</MobileCardTitle>
                    <Badge
                      bg={product.is_active ? 'success' : 'secondary'}
                      className={product.is_active ? '' : 'text-white'}
                    >
                      {product.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </MobileCardTop>
                  <MobileCardRow label="ID" mono>
                    {product.product_id}
                  </MobileCardRow>
                  <MobileCardRow label="Description">
                    {product.description || <span className="text-muted">No description</span>}
                  </MobileCardRow>
                  <MobileCardRow label="Price">
                    {product.price ? formatPrice(product.price, product.currency) : 'N/A'}
                  </MobileCardRow>
                  <MobileCardRow label="Credits">{product.credit_amount || 0}</MobileCardRow>
                  <MobileCardRow label="Created">
                    {product.created_at ? formatDate(product.created_at) : 'N/A'}
                  </MobileCardRow>
                  {(canEdit || canDelete) && (
                    <MobileCardFooter>
                      {canEdit && (
                        <>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleShowModal(product)}
                            title="Edit Product"
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </Button>
                          <Button
                            variant={product.is_active ? 'outline-warning' : 'outline-success'}
                            size="sm"
                            onClick={() => handleToggleActive(product.product_id)}
                            title={product.is_active ? 'Deactivate' : 'Activate'}
                          >
                            <FontAwesomeIcon icon={product.is_active ? faToggleOff : faToggleOn} />
                          </Button>
                        </>
                      )}
                      {canDelete && (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDelete(product.product_id)}
                          title="Delete Product"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </Button>
                      )}
                    </MobileCardFooter>
                  )}
                </MobileCard>
              ))}
            </ListMobile>
          )}

          <Pagination
            currentPage={currentPage}
            totalItems={total}
            itemsPerPage={pageSize}
            onPageChange={handlePageChange}
            position="bottom"
          />
        </Card.Body>
      </Card>

      {canEdit && (
        <ProductForm
          show={showModal}
          onHide={handleCloseModal}
          onSubmit={handleSubmit}
          editingProduct={editingProduct}
          loading={formLoading}
        />
      )}
    </div>
  );
};

export default ProductList;
